// components/Carousel.tsx  (Server Component)
import styles from './Carousel.module.css';
import type { CSSProperties, ReactNode } from 'react';
import CarouselClient from './CarouselClient';

export type CarouselItem = {
  id: string;
  content: ReactNode;
};

export type ResponsivePerView = {
  mobile?: number;
  tablet?: number;
  desktop?: number;
};

export type SnapMode = 'none' | 'item' | 'group';

export type ResponsiveSnapStep = {
  mobile?: 1 | 2 | 3 | 4;
  tablet?: 1 | 2 | 3 | 4;
  desktop?: 1 | 2 | 3 | 4;
};

export type ResponsiveSnapMode = {
  mobile?: SnapMode;
  tablet?: SnapMode;
  desktop?: SnapMode;
};

export type ResponsivePeek = {
  mobile?: number;
  tablet?: number;
  desktop?: number;
};

export type ResponsivePadStart = {
  mobile?: number;
  tablet?: number;
  desktop?: number;
};

export type ResponsivePadEnd = {
  mobile?: number;
  tablet?: number;
  desktop?: number;
};

type Props = {
  items: CarouselItem[];
  carouselId: string; // Unique ID for external controls
  perView?: number | ResponsivePerView;
  peek?: number | ResponsivePeek;
  padStart?: number | ResponsivePadStart;
  padEnd?: number | ResponsivePadEnd;
  gap?: number;
  /**
   * Snap behavior:
   * - `none`: free scroll, no snapping
   * - `item`: snap each item
   * - `group`: snap by groups (defaults to `perView`, clamped 1–4)
   */
  snap?: SnapMode | ResponsiveSnapMode;
  /** Group size for `snap="group"` (1–4). Defaults to `perView` rounded/clamped to 1–4. */
  snapStep?: 1 | 2 | 3 | 4 | ResponsiveSnapStep;
  /** When true, disables scroll snapping for free scrolling. Prefer `snap="none"`. */
  shouldFreeScroll?: boolean;
  className?: string;
  ariaLabel?: string;
  children?: ReactNode; // For dots, controls, or any other carousel-related components
};

function resolveResponsiveNumber(
  value: number | { mobile?: number; tablet?: number; desktop?: number } | undefined,
  defaults: { mobile: number; tablet: number; desktop: number },
) {
  if (typeof value === 'number') return { mobile: value, tablet: value, desktop: value };
  return { ...defaults, ...(value ?? {}) };
}

function resolveResponsiveSnapMode() {
  return { mobile: 'group', tablet: 'group', desktop: 'group' };
}

function clampSnapStep(step: number): 1 | 2 | 3 | 4 {
  const rounded = Math.round(step);
  const clamped = Math.max(1, Math.min(4, Number.isFinite(rounded) ? rounded : 1));
  return clamped as 1 | 2 | 3 | 4;
}

export default function Carousel({
  items,
  carouselId,
  perView = 1,
  peek = 0,
  padStart = 0,
  padEnd = 0,
  gap = 16,
  snap = { mobile: 'none', tablet: 'group', desktop: 'group' },
  snapStep,
  shouldFreeScroll,
  className,
  ariaLabel = 'Carousel',
  children,
}: Props) {
  const resolvedSnapMode =
    shouldFreeScroll === true
      ? { mobile: 'none', tablet: 'none', desktop: 'none' }
      : resolveResponsiveSnapMode();

  const resolvedPerView = resolveResponsiveNumber(perView, { mobile: 1, tablet: 2, desktop: 3 });
  const resolvedPeek = resolveResponsiveNumber(peek, { mobile: 0, tablet: 0, desktop: 0 });
  const resolvedPadStart = resolveResponsiveNumber(padStart, { mobile: 0, tablet: 0, desktop: 0 });
  const resolvedPadEnd = resolveResponsiveNumber(padEnd, { mobile: 0, tablet: 0, desktop: 0 });

  const resolvedSnapStep = (() => {
    if (typeof snapStep === 'number') {
      const step = clampSnapStep(snapStep);
      return { mobile: step, tablet: step, desktop: step };
    }
    if (snapStep && typeof snapStep === 'object') {
      return {
        mobile: clampSnapStep(snapStep.mobile ?? resolvedPerView.mobile),
        tablet: clampSnapStep(snapStep.tablet ?? resolvedPerView.tablet),
        desktop: clampSnapStep(snapStep.desktop ?? resolvedPerView.desktop),
      };
    }
    return {
      mobile: clampSnapStep(resolvedPerView.mobile),
      tablet: clampSnapStep(resolvedPerView.tablet),
      desktop: clampSnapStep(resolvedPerView.desktop),
    };
  })();

  const snapStepDataAttrs = {
    'data-snap-step-mobile': String(resolvedSnapStep.mobile),
    'data-snap-step-tablet': String(resolvedSnapStep.tablet),
    'data-snap-step-desktop': String(resolvedSnapStep.desktop),
  };

  const perViewDataAttrs = {
    'data-per-view-mobile': String(resolvedPerView.mobile),
    'data-per-view-tablet': String(resolvedPerView.tablet),
    'data-per-view-desktop': String(resolvedPerView.desktop),
    'data-gap': String(gap),
  };

  const snapModeDataAttrs = {
    'data-snap-mode-mobile': resolvedSnapMode.mobile,
    'data-snap-mode-tablet': resolvedSnapMode.tablet,
    'data-snap-mode-desktop': resolvedSnapMode.desktop,
  };

  // Handle responsive perView and peek configuration
  const getResponsiveStyles = () => {
    return {
      '--per-view-mobile': String(resolvedPerView.mobile),
      '--per-view-tablet': String(resolvedPerView.tablet),
      '--per-view-desktop': String(resolvedPerView.desktop),

      '--peek-mobile': `${resolvedPeek.mobile}px`,
      '--peek-tablet': `${resolvedPeek.tablet}px`,
      '--peek-desktop': `${resolvedPeek.desktop}px`,
      // `peek` should represent visible content peek (exclude slide gap).
      // Reserve the gap only when peeking is enabled so `peek={0}` doesn't shrink slides.
      '--peek-gap-mobile': resolvedPeek.mobile > 0 ? `${gap}px` : '0px',
      '--peek-gap-tablet': resolvedPeek.tablet > 0 ? `${gap}px` : '0px',
      '--peek-gap-desktop': resolvedPeek.desktop > 0 ? `${gap}px` : '0px',

      '--pad-start-mobile': `${resolvedPadStart.mobile}px`,
      '--pad-start-tablet': `${resolvedPadStart.tablet}px`,
      '--pad-start-desktop': `${resolvedPadStart.desktop}px`,

      '--pad-end-mobile': `${resolvedPadEnd.mobile}px`,
      '--pad-end-tablet': `${resolvedPadEnd.tablet}px`,
      '--pad-end-desktop': `${resolvedPadEnd.desktop}px`,
    };
  };

  const styleVars = {
    ...getResponsiveStyles(),
    '--gap': `${gap}px`,
  } as CSSProperties;

  return (
    <section
      className={`${styles.root} ${className ?? ''}`}
      aria-label={ariaLabel}
      style={styleVars}
      data-carousel-id={carouselId}
      {...snapModeDataAttrs}
      {...snapStepDataAttrs}
      {...perViewDataAttrs}
      id={carouselId}
    >
      <ul className={`${styles.track} ${carouselId}-track`} role="list">
        {items.map(item => (
          <li key={item.id} id={item.id} className={`${styles.slide} ${carouselId}-slide`}>
            {item.content}
          </li>
        ))}
      </ul>

      {/* Render children (dots, controls, etc.) inside the carousel container */}
      {children}

      {items.length > 1 ? <CarouselClient carouselId={carouselId} /> : null}
    </section>
  );
}
